import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Define the initial state
const initialState = {
    productCategoriesList: [],
    productCategoriesLoading: false,
    productCategoriesError: null,
    selectedCategory: null,
    selectedCategoryLoading: false,
    selectedCategoryError: null,
    createCategoryLoading: false,
    createCategoryError: null,
    updateCategoryLoading: false,
    updateCategoryError: null,
    deleteCategoryLoading: false,
    deleteCategoryError: null,
    productCategoriesTree: [],
    productCategoriesTreeLoading: false,
    productCategoriesTreeError: null,
    reorderLoading: false,
    reorderError: null
};

// Async actions
export const getProductCategoriesList = createAsyncThunk(
    "productCategory/getProductCategoriesList",
    async ({ accountId, token }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/products/categoriesList`, {
                headers: {
                    "auth-token": token
                },
                params: {
                    accountId
                }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nel recupero delle categorie di prodotti");
        }
    }
);

export const getProductCategoryDetail = createAsyncThunk(
    "productCategory/getProductCategoryDetail",
    async ({ categoryId, token }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/products/categoryDetail`, {
                headers: {
                    "auth-token": token
                },
                params: {
                    categoryId
                }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nel recupero dei dettagli della categoria");
        }
    }
);

export const createProductCategory = createAsyncThunk(
    "productCategory/createProductCategory",
    async ({ category, token }, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/products/categoryCreate`, {
                category
            }, {
                headers: {
                    "auth-token": token
                }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nella creazione della categoria");
        }
    }
);

export const updateProductCategory = createAsyncThunk(
    "productCategory/updateProductCategory",
    async ({ category, token }, { rejectWithValue }) => {
        try {
            const response = await axios.patch(`${import.meta.env.VITE_API_BASE_URL}/products/categorySave`, {
                category
            }, {
                headers: {
                    "auth-token": token
                }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nell'aggiornamento della categoria");
        }
    }
);

export const getProductCategoriesTree = createAsyncThunk(
    "productCategory/getProductCategoriesTree",
    async ({ accountId, token }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/products/categoriesTree`, {
                headers: {
                    "auth-token": token
                },
                params: {
                    accountId
                }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nel recupero dell'albero categorie");
        }
    }
);

export const reorderProductCategories = createAsyncThunk(
    "productCategory/reorderProductCategories",
    async ({ categories, token }, { rejectWithValue }) => {
        try {
            const response = await axios.patch(`${import.meta.env.VITE_API_BASE_URL}/products/categoriesReorder`, {
                categories
            }, {
                headers: {
                    "auth-token": token
                }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nel riordinamento delle categorie");
        }
    }
);

export const deleteProductCategory = createAsyncThunk(
    "productCategory/deleteProductCategory",
    async ({ categoryId, deleteProducts, token }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(`${import.meta.env.VITE_API_BASE_URL}/products/categoryDelete`, {
                headers: {
                    "auth-token": token
                },
                data: {
                    categoryId,
                    deleteProducts: deleteProducts === true
                }
            });
            return categoryId;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Errore nella cancellazione della categoria");
        }
    }
);

// Slice
const productCategorySlice = createSlice({
    name: "productCategory",
    initialState,
    reducers: {
        resetSelectedCategory: (state) => {
            state.selectedCategory = null;
            state.selectedCategoryError = null;
        },
        resetCreateStatus: (state) => {
            state.createCategoryLoading = false;
            state.createCategoryError = null;
        },
        resetUpdateStatus: (state) => {
            state.updateCategoryLoading = false;
            state.updateCategoryError = null;
        },
        resetDeleteStatus: (state) => {
            state.deleteCategoryLoading = false;
            state.deleteCategoryError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Gestione getProductCategoriesList
            .addCase(getProductCategoriesList.pending, (state) => {
                state.productCategoriesLoading = true;
                state.productCategoriesError = null;
            })
            .addCase(getProductCategoriesList.fulfilled, (state, action) => {
                state.productCategoriesLoading = false;
                state.productCategoriesList = action.payload;
            })
            .addCase(getProductCategoriesList.rejected, (state, action) => {
                state.productCategoriesLoading = false;
                state.productCategoriesError = action.payload;
            })
            
            // Gestione getProductCategoryDetail
            .addCase(getProductCategoryDetail.pending, (state) => {
                state.selectedCategoryLoading = true;
                state.selectedCategoryError = null;
            })
            .addCase(getProductCategoryDetail.fulfilled, (state, action) => {
                state.selectedCategoryLoading = false;
                state.selectedCategory = action.payload;
            })
            .addCase(getProductCategoryDetail.rejected, (state, action) => {
                state.selectedCategoryLoading = false;
                state.selectedCategoryError = action.payload;
            })
            
            // Gestione createProductCategory
            .addCase(createProductCategory.pending, (state) => {
                state.createCategoryLoading = true;
                state.createCategoryError = null;
            })
            .addCase(createProductCategory.fulfilled, (state, action) => {
                state.createCategoryLoading = false;
                state.productCategoriesList.push(action.payload);
            })
            .addCase(createProductCategory.rejected, (state, action) => {
                state.createCategoryLoading = false;
                state.createCategoryError = action.payload;
            })
            
            // Gestione updateProductCategory
            .addCase(updateProductCategory.pending, (state) => {
                state.updateCategoryLoading = true;
                state.updateCategoryError = null;
            })
            .addCase(updateProductCategory.fulfilled, (state, action) => {
                state.updateCategoryLoading = false;
                const updatedIndex = state.productCategoriesList.findIndex(cat => cat._id === action.payload._id);
                if (updatedIndex !== -1) {
                    state.productCategoriesList[updatedIndex] = action.payload;
                }
                state.selectedCategory = action.payload;
            })
            .addCase(updateProductCategory.rejected, (state, action) => {
                state.updateCategoryLoading = false;
                state.updateCategoryError = action.payload;
            })
            
            // Gestione getProductCategoriesTree
            .addCase(getProductCategoriesTree.pending, (state) => {
                state.productCategoriesTreeLoading = true;
                state.productCategoriesTreeError = null;
            })
            .addCase(getProductCategoriesTree.fulfilled, (state, action) => {
                state.productCategoriesTreeLoading = false;
                state.productCategoriesTree = action.payload;
            })
            .addCase(getProductCategoriesTree.rejected, (state, action) => {
                state.productCategoriesTreeLoading = false;
                state.productCategoriesTreeError = action.payload;
            })

            // Gestione reorderProductCategories
            .addCase(reorderProductCategories.pending, (state) => {
                state.reorderLoading = true;
                state.reorderError = null;
            })
            .addCase(reorderProductCategories.fulfilled, (state) => {
                state.reorderLoading = false;
            })
            .addCase(reorderProductCategories.rejected, (state, action) => {
                state.reorderLoading = false;
                state.reorderError = action.payload;
            })

            // Gestione deleteProductCategory
            .addCase(deleteProductCategory.pending, (state) => {
                state.deleteCategoryLoading = true;
                state.deleteCategoryError = null;
            })
            .addCase(deleteProductCategory.fulfilled, (state, action) => {
                state.deleteCategoryLoading = false;
                state.productCategoriesList = state.productCategoriesList.filter(cat => cat._id !== action.payload);
            })
            .addCase(deleteProductCategory.rejected, (state, action) => {
                state.deleteCategoryLoading = false;
                state.deleteCategoryError = action.payload;
            });
    }
});

// Export actions
export const { 
    resetSelectedCategory, 
    resetCreateStatus, 
    resetUpdateStatus, 
    resetDeleteStatus 
} = productCategorySlice.actions;

// Export selectors
export const selectProductCategoriesList = (state) => state.productCategory.productCategoriesList;
export const selectProductCategoriesLoading = (state) => state.productCategory.productCategoriesLoading;
export const selectProductCategoriesError = (state) => state.productCategory.productCategoriesError;
export const selectSelectedCategory = (state) => state.productCategory.selectedCategory;
export const selectSelectedCategoryLoading = (state) => state.productCategory.selectedCategoryLoading;
export const selectSelectedCategoryError = (state) => state.productCategory.selectedCategoryError;
export const selectCreateCategoryLoading = (state) => state.productCategory.createCategoryLoading;
export const selectCreateCategoryError = (state) => state.productCategory.createCategoryError;
export const selectUpdateCategoryLoading = (state) => state.productCategory.updateCategoryLoading;
export const selectUpdateCategoryError = (state) => state.productCategory.updateCategoryError;
export const selectDeleteCategoryLoading = (state) => state.productCategory.deleteCategoryLoading;
export const selectDeleteCategoryError = (state) => state.productCategory.deleteCategoryError;

export const selectProductCategoriesTree = (state) => state.productCategory.productCategoriesTree;
export const selectProductCategoriesTreeLoading = (state) => state.productCategory.productCategoriesTreeLoading;
export const selectProductCategoriesTreeError = (state) => state.productCategory.productCategoriesTreeError;

export const selectReorderLoading = (state) => state.productCategory.reorderLoading;
export const selectReorderError = (state) => state.productCategory.reorderError;

// Export reducer
export default productCategorySlice.reducer;
